'use client';

import { useParams } from 'next/navigation';
import ServiceSticker, {
    getServiceStickerKind,
    type ServiceStickerKind,
    type ServiceStickerVariant,
} from '../vectors/ServiceSticker';
import StickerPeel from '../animations/StickerPeel';

const validKinds: ServiceStickerKind[] = [
    'creative-studio',
    'growth-systems',
    'paid-ads-performance',
    'strategy-brand-building',
];

const validVariants: ServiceStickerVariant[] = ['blue', 'red', 'green', 'orange'];

function normalizeKind(kind?: string | null) {
    return validKinds.includes(kind as ServiceStickerKind) ? kind as ServiceStickerKind : undefined;
}

function normalizeVariant(variant?: string | null) {
    return validVariants.includes(variant as ServiceStickerVariant) ? variant as ServiceStickerVariant : undefined;
}

interface ServiceStickerBadgeProps {
    slug?: string | null;
    kind?: string | null;
    variant?: string | null;
    className?: string;
    title?: string;
    align?: 'start' | 'end' | 'center';
    sizeClassName?: string;
    locale?: string;
}

export default function ServiceStickerBadge({
    slug,
    kind,
    variant,
    className = '',
    title,
    align = 'end',
    sizeClassName = 'w-32 sm:w-40 md:w-44',
    locale,
}: ServiceStickerBadgeProps) {
    const params = useParams();
    const resolvedLocale = locale || (params?.locale as string) || 'ar';
    const isAr = resolvedLocale === 'ar';

    const stickerKind = normalizeKind(kind) || getServiceStickerKind(slug);
    const stickerVariant = normalizeVariant(variant);

    // The bounds div below is the draggable arena (matches react-bits'
    // StickerPeel, which bounds dragging to its immediate parent) — it's
    // pointer-events-none so it never blocks clicks on the card underneath;
    // only the sticker's own DOM node (pointer-events-auto) is interactive.
    // `align` picks which side of the card that arena occupies, so the
    // sticker still starts roughly where the old badge used to sit.
    const boundsClass = align === 'start'
        ? 'inset-y-3 left-3 w-1/2 max-w-[220px]'
        : align === 'center'
        ? 'inset-3'
        : 'inset-y-3 right-3 w-1/2 max-w-[220px]';

    const baseRotate = align === 'start' ? -8 : align === 'center' ? -3 : 8;
    const rotate = isAr ? -baseRotate : baseRotate;

    return (
        <div className={`pointer-events-none absolute z-30 ${boundsClass} ${className}`}>
            <StickerPeel
                className="pointer-events-auto"
                contentClassName={sizeClassName}
                rotate={rotate}
                peelBackHoverPct={35}
                peelBackActivePct={45}
                shadowIntensity={0.5}
                lightingIntensity={0.15}
            >
                <ServiceSticker
                    kind={stickerKind}
                    variant={stickerVariant}
                    title={title}
                    className="h-auto w-full"
                    initial={false}
                />
            </StickerPeel>
        </div>
    );
}
